import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../App';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Sparkles, MessageSquare, Save, Plus, X, Eye, Palette, Settings as SettingsIcon, Bot, LayoutGrid, Home, MessageCircle, Newspaper, Map as MapIcon, HelpCircle, Trash2, Copy, Check, Zap, Edit3, ChevronDown, ChevronRight, ChevronUp } from 'lucide-react';
import PublicChatWidget from '../components/PublicChatWidget';
import { toast } from 'sonner';

const TONES = [
  { id: 'friendly', label: 'Friendly', desc: 'Warm, approachable' },
  { id: 'formal', label: 'Formal', desc: 'Professional, precise' },
  { id: 'technical', label: 'Technical', desc: 'Direct, technical' },
  { id: 'casual', label: 'Casual', desc: 'Relaxed, colleague-like' },
];

const ChatbotSettings = ({ user, setUser }) => {
  const [workspace, setWorkspace] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    chatbot_name: 'Helpdesk AI',
    chatbot_avatar_url: '',
    primary_color: '#7c3aed',
    ai_button_text: 'Ask AI',
    chat_welcome_heading: 'Hi 👋',
    chat_welcome_subheading: 'How can we help you today?',
    chat_input_placeholder: 'Ask me anything…',
    chat_quick_prompts: [],
    chat_tone: 'friendly',
    chat_persona_instructions: '',
    chat_show_sources: true,
    chat_show_suggested_followups: true,
    chat_max_response_words: 150,
    chat_no_answer_action: 'show_contact',
    ai_fallback_message: "I couldn't find a specific answer to your question.",
    enable_contact_form: true,
    contact_form_title: 'Contact Support',
    contact_form_description: "Fill out this form and we'll get back to you soon.",
    show_ai_chat: true,
    chat_modules: { home: true, messages: true, news: true, roadmap: true, help: true, human_chat: false },
    chat_quick_actions: [],
  });
  const [newPrompt, setNewPrompt] = useState('');
  const [bulkPromptMode, setBulkPromptMode] = useState(false);
  const [newAction, setNewAction] = useState({ label: '', icon: '', action: 'form', target: '' });
  const [forms, setForms] = useState([]);

  useEffect(() => { fetchWorkspace(); fetchForms(); }, []);

  const fetchWorkspace = async () => {
    try {
      const res = await axios.get(`${API_URL}/workspaces/my`);
      setWorkspace(res.data);
      const s = res.data.settings || {};
      setForm(prev => ({
        ...prev,
        ...s,
        chat_quick_prompts: s.chat_quick_prompts || [],
        chat_modules: { home: true, messages: true, news: true, roadmap: true, help: true, human_chat: false, ...(s.chat_modules || {}) },
        chat_quick_actions: s.chat_quick_actions || [],
      }));
    } catch (e) {
      toast.error('Failed to load workspace');
    }
  };

  const fetchForms = async () => {
    try { const r = await axios.get(`${API_URL}/forms`); setForms(r.data.forms || []); } catch (_) { void _; }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/workspaces/${workspace.id}/settings`, form);
      toast.success('Chatbot settings saved');
      fetchWorkspace();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const addPrompt = () => {
    if (!newPrompt.trim()) return;
    if ((form.chat_quick_prompts || []).length >= 4) {
      toast.error('Maximum 4 quick prompts');
      return;
    }
    setForm({ ...form, chat_quick_prompts: [...(form.chat_quick_prompts || []), newPrompt.trim()] });
    setNewPrompt('');
  };

  const removePrompt = (idx) => {
    setForm({ ...form, chat_quick_prompts: (form.chat_quick_prompts || []).filter((_, i) => i !== idx) });
  };

  if (!workspace) return <Layout user={user} setUser={setUser}><div className="p-8">Loading…</div></Layout>;

  const gradient = `linear-gradient(135deg, ${form.primary_color} 0%, ${form.primary_color}cc 50%, ${form.primary_color}88 100%)`;

  return (
    <Layout user={user} setUser={setUser}>
      <div className="p-8 max-w-7xl mx-auto" data-testid="chatbot-settings-page">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-violet-500 to-fuchsia-500">
                <Bot className="h-5 w-5 text-white" />
              </div>
              <h1 className="text-4xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>
                Chatbot Settings
              </h1>
            </div>
            <p className="text-sm text-zinc-600 ml-13">
              Customize the AI Helpdesk experience for <strong>{workspace.name}</strong>.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.location.href = '/chatbot-builder'} data-testid="open-flow-builder-button">
              <Sparkles className="h-4 w-4 mr-2" />Flow Builder
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="chatbot-save-button">
              <Save className="h-4 w-4 mr-2" />{saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form */}
          <div className="lg:col-span-2 space-y-4">
            <Tabs defaultValue="appearance">
              <TabsList className="grid grid-cols-8 max-w-4xl">
                <TabsTrigger value="appearance" data-testid="tab-appearance"><Palette className="h-4 w-4 mr-1" />Look</TabsTrigger>
                <TabsTrigger value="modules" data-testid="tab-modules"><LayoutGrid className="h-4 w-4 mr-1" />Modules</TabsTrigger>
                <TabsTrigger value="behavior" data-testid="tab-behavior"><MessageSquare className="h-4 w-4 mr-1" />Behavior</TabsTrigger>
                <TabsTrigger value="prompts" data-testid="tab-prompts"><Sparkles className="h-4 w-4 mr-1" />Prompts</TabsTrigger>
                <TabsTrigger value="proactive" data-testid="tab-proactive"><Zap className="h-4 w-4 mr-1" />Proactive</TabsTrigger>
                <TabsTrigger value="advanced" data-testid="tab-advanced"><SettingsIcon className="h-4 w-4 mr-1" />Advanced</TabsTrigger>
                <TabsTrigger value="test" data-testid="tab-test"><MessageCircle className="h-4 w-4 mr-1" />Test</TabsTrigger>
                <TabsTrigger value="embed" data-testid="tab-embed"><Bot className="h-4 w-4 mr-1" />Install</TabsTrigger>
              </TabsList>

              <TabsContent value="appearance" className="bg-white border border-zinc-200 rounded-2xl p-5 space-y-4 mt-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium">Chatbot Name</Label>
                    <Input value={form.chatbot_name} onChange={e => setForm({...form, chatbot_name: e.target.value})} className="mt-1.5" data-testid="chatbot-name-input" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Launcher Button Text</Label>
                    <Input value={form.ai_button_text} onChange={e => setForm({...form, ai_button_text: e.target.value})} className="mt-1.5" />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Welcome Heading</Label>
                  <Input value={form.chat_welcome_heading} onChange={e => setForm({...form, chat_welcome_heading: e.target.value})} className="mt-1.5" data-testid="chatbot-welcome-heading" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Welcome Subheading</Label>
                  <Input value={form.chat_welcome_subheading} onChange={e => setForm({...form, chat_welcome_subheading: e.target.value})} className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Input Placeholder</Label>
                  <Input value={form.chat_input_placeholder} onChange={e => setForm({...form, chat_input_placeholder: e.target.value})} className="mt-1.5" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium">Primary Color</Label>
                    <div className="flex gap-2 mt-1.5">
                      <input type="color" value={form.primary_color} onChange={e => setForm({...form, primary_color: e.target.value})} className="h-10 w-16 rounded border" data-testid="chatbot-color-input" />
                      <Input value={form.primary_color} onChange={e => setForm({...form, primary_color: e.target.value})} />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Avatar URL (optional)</Label>
                    <Input value={form.chatbot_avatar_url || ''} onChange={e => setForm({...form, chatbot_avatar_url: e.target.value})} placeholder="https://…" className="mt-1.5" />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="modules" className="bg-white border border-zinc-200 rounded-2xl p-5 space-y-4 mt-3">
                <div>
                  <h3 className="text-sm font-bold text-zinc-900 mb-1">Widget Tabs</h3>
                  <p className="text-xs text-zinc-500 mb-3">Toggle which sections appear inside the chatbot widget.</p>
                  <div className="space-y-2">
                    {[
                      { id: 'home', label: 'Home', icon: Home, desc: 'Welcome screen + Ask AI + quick actions' },
                      { id: 'messages', label: 'Messages', icon: MessageCircle, desc: 'Past conversations + send a message' },
                      { id: 'news', label: 'News', icon: Newspaper, desc: 'Announcements & release notes' },
                      { id: 'roadmap', label: 'Roadmap', icon: MapIcon, desc: 'Public roadmap with upvotes' },
                      { id: 'help', label: 'Help', icon: HelpCircle, desc: 'Searchable helpdesk articles' },
                    ].map(m => {
                      const Icon = m.icon;
                      const enabled = form.chat_modules?.[m.id] !== false;
                      return (
                        <div key={m.id} className="flex items-center justify-between p-3 rounded-xl border border-zinc-200" data-testid={`module-row-${m.id}`}>
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-lg bg-violet-50 flex items-center justify-center">
                              <Icon className="h-4 w-4 text-violet-600" />
                            </div>
                            <div>
                              <div className="text-sm font-semibold text-zinc-900">{m.label}</div>
                              <div className="text-xs text-zinc-500">{m.desc}</div>
                            </div>
                          </div>
                          <Switch
                            checked={enabled}
                            onCheckedChange={v => setForm({ ...form, chat_modules: { ...form.chat_modules, [m.id]: v } })}
                            data-testid={`module-toggle-${m.id}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="pt-2">
                  <h3 className="text-sm font-bold text-zinc-900 mb-1">Live Chat with Human (Coming Soon)</h3>
                  <div className="flex items-center justify-between p-3 rounded-xl border border-zinc-200 opacity-75">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                        <MessageSquare className="h-4 w-4 text-emerald-600" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-zinc-900">Chat with a human</div>
                        <div className="text-xs text-zinc-500">Show &quot;Send us a message&quot; CTA in Messages tab</div>
                      </div>
                    </div>
                    <Switch
                      checked={form.chat_modules?.human_chat || false}
                      onCheckedChange={v => setForm({ ...form, chat_modules: { ...form.chat_modules, human_chat: v } })}
                      data-testid="module-toggle-human_chat"
                    />
                  </div>
                </div>

                <div className="pt-3 border-t border-zinc-100">
                  <h3 className="text-sm font-bold text-zinc-900 mb-1">Home Quick Actions</h3>
                  <p className="text-xs text-zinc-500 mb-3">Buttons shown on the Home tab below the AI input.</p>
                  <div className="space-y-2 mb-3">
                    {(form.chat_quick_actions || []).map((a, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 bg-zinc-50 rounded-lg" data-testid={`quick-action-row-${i}`}>
                        <span className="text-base w-7 text-center">{a.icon || '🔹'}</span>
                        <span className="flex-1 text-sm font-medium">{a.label}</span>
                        <span className="text-xs text-zinc-500">→ {a.action === 'form' ? `Form: ${a.target}` : a.target}</span>
                        <button
                          onClick={() => setForm({ ...form, chat_quick_actions: form.chat_quick_actions.filter((_, idx) => idx !== i) })}
                          className="text-zinc-400 hover:text-red-500"
                        ><Trash2 className="h-4 w-4" /></button>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-12 gap-2">
                    <Input
                      value={newAction.icon}
                      onChange={e => setNewAction({ ...newAction, icon: e.target.value })}
                      placeholder="🐞"
                      className="col-span-1 text-center"
                      maxLength={2}
                    />
                    <Input
                      value={newAction.label}
                      onChange={e => setNewAction({ ...newAction, label: e.target.value })}
                      placeholder="Label (e.g. Report an issue)"
                      className="col-span-4"
                      data-testid="qa-label-input"
                    />
                    <select
                      value={newAction.action}
                      onChange={e => setNewAction({ ...newAction, action: e.target.value, target: '' })}
                      className="col-span-2 h-10 px-2 rounded-md border border-zinc-200 bg-white text-sm"
                    >
                      <option value="form">Form</option>
                      <option value="url">URL</option>
                    </select>
                    {newAction.action === 'form' ? (
                      <select
                        value={newAction.target}
                        onChange={e => setNewAction({ ...newAction, target: e.target.value })}
                        className="col-span-4 h-10 px-2 rounded-md border border-zinc-200 bg-white text-sm"
                        data-testid="qa-form-select"
                      >
                        <option value="">Choose form…</option>
                        {forms.map(f => <option key={f.id} value={f.slug}>{f.title}</option>)}
                      </select>
                    ) : (
                      <Input
                        value={newAction.target}
                        onChange={e => setNewAction({ ...newAction, target: e.target.value })}
                        placeholder="https://…"
                        className="col-span-4"
                      />
                    )}
                    <Button
                      onClick={() => {
                        if (!newAction.label || !newAction.target) return;
                        setForm({ ...form, chat_quick_actions: [...(form.chat_quick_actions || []), newAction] });
                        setNewAction({ label: '', icon: '', action: 'form', target: '' });
                      }}
                      variant="outline"
                      className="col-span-1 px-2"
                      data-testid="qa-add-button"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="behavior" className="bg-white border border-zinc-200 rounded-2xl p-5 space-y-4 mt-3">
                <div>
                  <Label className="text-sm font-medium">Bot Tone</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1.5">
                    {TONES.map(t => (
                      <button
                        key={t.id}
                        onClick={() => setForm({...form, chat_tone: t.id})}
                        className={`px-3 py-3 rounded-xl text-left border-2 transition-all ${form.chat_tone === t.id ? 'border-violet-400 bg-violet-50' : 'border-zinc-200 hover:border-zinc-300'}`}
                        data-testid={`tone-${t.id}`}
                      >
                        <div className="font-semibold text-sm">{t.label}</div>
                        <div className="text-xs text-zinc-500">{t.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Max Response Length (words)</Label>
                  <Input type="number" min={50} max={500} value={form.chat_max_response_words} onChange={e => setForm({...form, chat_max_response_words: parseInt(e.target.value) || 150})} className="mt-1.5" />
                  <p className="text-xs text-zinc-500 mt-1">Shorter = snappier replies. 150 is a good default.</p>
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl border border-zinc-200">
                  <div>
                    <div className="text-sm font-semibold">Show source articles</div>
                    <div className="text-xs text-zinc-500">Display source-citation cards under AI answers</div>
                  </div>
                  <Switch checked={!!form.chat_show_sources} onCheckedChange={v => setForm({...form, chat_show_sources: v})} data-testid="toggle-show-sources" />
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl border border-zinc-200">
                  <div>
                    <div className="text-sm font-semibold">Show chatbot widget on portal</div>
                    <div className="text-xs text-zinc-500">Toggle the floating &quot;Ask AI&quot; button on the public portal</div>
                  </div>
                  <Switch checked={!!form.show_ai_chat} onCheckedChange={v => setForm({...form, show_ai_chat: v})} />
                </div>
              </TabsContent>

              <TabsContent value="prompts" className="bg-white border border-zinc-200 rounded-2xl p-5 space-y-4 mt-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-sm font-medium">Quick Prompts (max 4)</Label>
                    <button
                      type="button"
                      onClick={() => setBulkPromptMode(!bulkPromptMode)}
                      className="text-xs font-semibold text-violet-600 hover:text-violet-700"
                      data-testid="toggle-bulk-prompt-mode"
                    >
                      {bulkPromptMode ? 'Switch to one-at-a-time' : 'Paste comma-separated'}
                    </button>
                  </div>
                  <p className="text-xs text-zinc-500 mb-3">Shown as clickable chips on the chatbot welcome screen.</p>

                  {bulkPromptMode ? (
                    <div className="space-y-2">
                      <Textarea
                        value={(form.chat_quick_prompts || []).join(', ')}
                        onChange={e => {
                          const arr = e.target.value.split(',').map(s => s.trim()).filter(Boolean).slice(0, 4);
                          setForm({ ...form, chat_quick_prompts: arr });
                        }}
                        placeholder="How do I reset my password?, Where is my dashboard?, How do I cancel?"
                        rows={3}
                        className="text-sm"
                        data-testid="bulk-prompts-textarea"
                      />
                      <p className="text-xs text-zinc-500">Separate with commas. First 4 are used.</p>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2 mb-3">
                        {(form.chat_quick_prompts || []).map((p, i) => (
                          <div key={i} className="flex items-center gap-2 px-3 py-2 bg-zinc-50 rounded-lg border border-zinc-200">
                            <span className="flex-1 text-sm">{p}</span>
                            <button onClick={() => removePrompt(i)} className="text-zinc-400 hover:text-red-500" data-testid={`remove-prompt-${i}`}>
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Input value={newPrompt} onChange={e => setNewPrompt(e.target.value)} placeholder="e.g. How do I reset my password?" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addPrompt())} data-testid="new-prompt-input" />
                        <Button onClick={addPrompt} variant="outline" data-testid="add-prompt-button"><Plus className="h-4 w-4" /></Button>
                      </div>
                    </>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="proactive" className="bg-white border border-zinc-200 rounded-2xl p-5 space-y-4 mt-3">
                <ProactivePanel
                  messages={form.chat_proactive_messages || []}
                  onChange={(msgs) => setForm({ ...form, chat_proactive_messages: msgs })}
                  forms={forms}
                />
              </TabsContent>

              <TabsContent value="advanced" className="bg-white border border-zinc-200 rounded-2xl p-5 space-y-4 mt-3">
                <div>
                  <Label className="text-sm font-medium">Persona Instructions (Optional)</Label>
                  <Textarea
                    value={form.chat_persona_instructions || ''}
                    onChange={e => setForm({...form, chat_persona_instructions: e.target.value})}
                    rows={4}
                    placeholder="e.g. Always recommend our Pro plan when discussing limits. Sign off as 'The Acme Team'."
                    className="mt-1.5"
                    data-testid="persona-input"
                  />
                  <p className="text-xs text-zinc-500 mt-1">Extra rules injected into the AI system prompt. Be specific.</p>
                </div>
                <div>
                  <Label className="text-sm font-medium">Fallback Message (when no answer found)</Label>
                  <Textarea value={form.ai_fallback_message || ''} onChange={e => setForm({...form, ai_fallback_message: e.target.value})} rows={2} className="mt-1.5" />
                </div>
                <div className="flex items-center justify-between p-3 rounded-xl border border-zinc-200">
                  <div>
                    <div className="text-sm font-semibold">Enable contact form fallback</div>
                    <div className="text-xs text-zinc-500">Show &quot;Contact support&quot; button when AI can&apos;t answer</div>
                  </div>
                  <Switch checked={!!form.enable_contact_form} onCheckedChange={v => setForm({...form, enable_contact_form: v})} data-testid="toggle-contact-form" />
                </div>
                {form.enable_contact_form && (
                  <>
                    <div>
                      <Label className="text-sm font-medium">Contact Form Title</Label>
                      <Input value={form.contact_form_title || ''} onChange={e => setForm({...form, contact_form_title: e.target.value})} className="mt-1.5" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Contact Form Description</Label>
                      <Textarea value={form.contact_form_description || ''} onChange={e => setForm({...form, contact_form_description: e.target.value})} rows={2} className="mt-1.5" />
                    </div>
                  </>
                )}
              </TabsContent>

              <TabsContent value="test" className="bg-white border border-zinc-200 rounded-2xl p-5 space-y-3 mt-3">
                <div>
                  <h3 className="text-sm font-bold text-zinc-900">Test the bot</h3>
                  <p className="text-xs text-zinc-500 mt-1">Send messages to the live chatbot — uses your current settings + helpdesk content.</p>
                </div>
                <TestBotSandbox subdomain={workspace.subdomain} primaryColor={form.primary_color} botName={form.chatbot_name} />
              </TabsContent>

              <TabsContent value="embed" className="bg-white border border-zinc-200 rounded-2xl p-5 space-y-4 mt-3">
                <div>
                  <h3 className="text-sm font-bold text-zinc-900 mb-1">Install on your website</h3>
                  <p className="text-xs text-zinc-500 mb-3">Paste this snippet just before <code>&lt;/body&gt;</code> on any page to add the Helpdesk widget.</p>
                  <EmbedSnippet
                    apiBase={(typeof window !== 'undefined') ? window.location.origin : ''}
                    subdomain={workspace.subdomain}
                    color={form.primary_color}
                    buttonText={form.ai_button_text}
                  />
                </div>
                <div className="pt-3 border-t border-zinc-100">
                  <h3 className="text-sm font-bold text-zinc-900 mb-1">Direct portal link</h3>
                  <p className="text-xs text-zinc-500 mb-2">Share this URL for a standalone helpdesk page.</p>
                  <CopyableField value={`${(typeof window !== 'undefined') ? window.location.origin : ''}/portal/${workspace.subdomain}`} testid="portal-url" />
                </div>
                <div className="pt-3 border-t border-zinc-100">
                  <h3 className="text-sm font-bold text-zinc-900 mb-1">Private CRM Link</h3>
                  <p className="text-xs text-zinc-500">For embedded CRM access without user accounts, generate a Private Access Key in Workspace Settings → Access Keys, then append <code>?key=YOUR_KEY</code> to the portal URL.</p>
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Live Preview — real widget rendered inline */}
          <div className="lg:col-span-1">
            <div className="sticky top-6">
              <div className="flex items-center gap-2 mb-3">
                <Eye className="h-4 w-4 text-zinc-500" />
                <span className="text-sm font-semibold text-zinc-700">Live Preview</span>
                <span className="text-[10px] uppercase tracking-wider text-zinc-400 ml-auto">Live</span>
              </div>
              <LivePreviewWidget
                subdomain={workspace.subdomain}
                workspaceName={workspace.name}
                primaryColor={form.primary_color}
                settings={form}
              />
              {(form.chat_proactive_messages || []).filter(m => m.enabled !== false).length > 0 && (
                <p className="text-[11px] text-zinc-500 mt-3 text-center">
                  + {(form.chat_proactive_messages || []).filter(m => m.enabled !== false).length} proactive message(s) will fire on the live site.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

/**
 * Renders the real PublicChatWidget inline, scaled to fit the sidebar.
 * Re-mounts whenever settings change so the welcome state always reflects edits.
 */
const LivePreviewWidget = ({ subdomain, workspaceName, primaryColor, settings }) => {
  // Key derived from settings to force remount on changes (lightweight diff)
  const settingsKey = React.useMemo(() => JSON.stringify({
    h: settings.chat_welcome_heading,
    s: settings.chat_welcome_subheading,
    p: settings.chat_input_placeholder,
    n: settings.chatbot_name,
    qp: settings.chat_quick_prompts,
    qa: settings.chat_quick_actions,
    m: settings.chat_modules,
    c: settings.primary_color,
    cs: settings.chat_show_sources,
    tone: settings.chat_tone,
  }), [settings]);

  return (
    <div className="relative bg-zinc-50 rounded-2xl overflow-hidden border border-zinc-200" style={{ height: '560px' }} data-testid="live-preview-widget">
      <div className="absolute inset-0" style={{ transform: 'scale(0.82)', transformOrigin: 'top right' }}>
        <PublicChatWidget
          key={settingsKey}
          subdomain={subdomain}
          workspaceName={workspaceName}
          primaryColor={primaryColor}
          onClose={() => {}}
          settings={settings}
          accessKey=""
          inline={true}
        />
      </div>
    </div>
  );
};

const CopyableField = ({ value, testid }) => {
  const [copied, setCopied] = React.useState(false);
  const onCopy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_) { void _; }
  };
  return (
    <div className="flex gap-2" data-testid={testid}>
      <code className="flex-1 px-3 py-2 bg-zinc-900 text-zinc-100 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre">{value}</code>
      <Button variant="outline" onClick={onCopy} className="px-3" data-testid={`${testid}-copy`}>
        {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  );
};

const EmbedSnippet = ({ apiBase, subdomain, color, buttonText }) => {
  const snippet = `<script src="${apiBase}/widget.js"
  data-workspace="${subdomain}"
  data-color="${color || '#7c3aed'}"
  data-button-text="${buttonText || 'Help'}"
  data-position="bottom-right"></script>`;
  return <CopyableField value={snippet} testid="embed-snippet" />;
};

const PROACTIVE_TYPES = [
  { id: 'text', label: 'Text', icon: '💬', desc: 'Plain message bubble' },
  { id: 'link', label: 'Link', icon: '🔗', desc: 'Message + external link button' },
  { id: 'calendar', label: 'Calendar Apt', icon: '📅', desc: 'Booking link (Calendly, Cal.com)' },
  { id: 'iframe', label: 'Iframe', icon: '🖼️', desc: 'Embed external content (Calendly inline, video, demo)' },
  { id: 'form', label: 'Form', icon: '📋', desc: 'Open a custom form created in Forms' },
];

const newProactive = (type = 'text') => {
  const base = {
    id: `pm-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type,
    enabled: true,
    trigger: 'after_delay',
    delay_seconds: 5,
    show_once_per_session: true,
    greeting_title: 'Welcome! 👋',
    message: 'Have questions? Tap below to learn more.',
    avatar_initial: 'A',
    sender_name: 'Support',
  };
  if (type === 'link') return { ...base, link_url: 'https://', link_button_label: 'Learn more' };
  if (type === 'calendar') return { ...base, booking_url: 'https://calendly.com/your-link', button_label: 'Pick a time', message: 'Need a demo?' };
  if (type === 'iframe') return { ...base, iframe_url: 'https://calendly.com/your-link?embed_domain=yes&hide_event_type_details=1', iframe_height: 360, message: '' };
  if (type === 'form') return { ...base, form_slug: '', button_label: 'Open form' };
  return base;
};

const ProactivePanel = ({ messages, onChange, forms = [] }) => {
  const [expandedId, setExpandedId] = React.useState(null);

  const updateMsg = (idx, patch) => {
    const next = [...messages];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const removeMsg = (idx) => onChange(messages.filter((_, i) => i !== idx));
  const addMsg = (type) => {
    const m = newProactive(type);
    onChange([...messages, m]);
    setExpandedId(m.id);
  };
  const toggleExpand = (id) => setExpandedId(expandedId === id ? null : id);

  const typeMeta = (t) => PROACTIVE_TYPES.find(x => x.id === t) || PROACTIVE_TYPES[0];

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-bold text-zinc-900">Proactive Messages</h3>
        <p className="text-xs text-zinc-500 mt-1">
          Pop-up messages that appear next to the closed launcher when visitors land on your site. 5 types: text, link, calendar, iframe, form.
        </p>
      </div>

      {/* List */}
      <div className="space-y-2">
        {messages.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed border-zinc-200 rounded-2xl bg-zinc-50/40">
            <Zap className="h-8 w-8 mx-auto text-zinc-300 mb-2" />
            <p className="text-sm text-zinc-500">No proactive messages yet — pick a type below</p>
          </div>
        ) : (
          messages.map((msg, idx) => {
            const meta = typeMeta(msg.type);
            const isOpen = expandedId === msg.id;
            return (
              <div key={msg.id || idx} className="border border-zinc-200 rounded-xl bg-white overflow-hidden" data-testid={`proactive-row-${idx}`}>
                {/* Collapsed summary row */}
                <div className="flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-50/60 transition-colors">
                  <span className="text-xl flex-shrink-0">{meta.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700">{meta.label}</span>
                      <span className="text-sm font-semibold text-zinc-900 truncate">{msg.greeting_title || meta.label}</span>
                    </div>
                    <div className="text-xs text-zinc-500 truncate mt-0.5">
                      {msg.message || msg.card_title || msg.link_url || msg.booking_url || msg.iframe_url || '—'}
                      <span className="text-zinc-400 ml-2">· {msg.trigger || 'after_delay'} · {msg.delay_seconds || 5}s</span>
                    </div>
                  </div>
                  <Switch checked={msg.enabled !== false} onCheckedChange={v => updateMsg(idx, { enabled: v })} data-testid={`proactive-toggle-${idx}`} />
                  <button onClick={() => toggleExpand(msg.id)} className="p-1.5 hover:bg-zinc-100 rounded-md text-zinc-500" data-testid={`proactive-expand-${idx}`}>
                    {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                  <button onClick={() => removeMsg(idx)} className="p-1.5 hover:bg-red-50 rounded-md text-red-500" data-testid={`proactive-remove-${idx}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                {/* Expanded editor */}
                {isOpen && (
                  <div className="border-t border-zinc-100 p-4 space-y-3 bg-zinc-50/30" data-testid={`proactive-editor-${idx}`}>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-medium">Type</Label>
                        <select
                          value={msg.type}
                          onChange={e => updateMsg(idx, { type: e.target.value })}
                          className="mt-1 w-full h-9 px-2 rounded-md border border-zinc-200 bg-white text-sm"
                        >
                          {PROACTIVE_TYPES.map(t => <option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs font-medium">Sender Name</Label>
                        <Input value={msg.sender_name || ''} onChange={e => updateMsg(idx, { sender_name: e.target.value })} className="mt-1 h-9" />
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs font-medium">Greeting (with emoji)</Label>
                      <Input value={msg.greeting_title || ''} onChange={e => updateMsg(idx, { greeting_title: e.target.value })} placeholder="Welcome! 👋" className="mt-1 h-9" />
                    </div>

                    {msg.type !== 'iframe' && (
                      <div>
                        <Label className="text-xs font-medium">Message body</Label>
                        <Textarea value={msg.message || ''} onChange={e => updateMsg(idx, { message: e.target.value })} rows={2} className="mt-1" data-testid={`proactive-message-${idx}`} />
                      </div>
                    )}

                    {/* Type-specific fields */}
                    {msg.type === 'link' && (
                      <div className="grid grid-cols-2 gap-2 p-3 rounded-lg bg-white border border-zinc-200">
                        <div>
                          <Label className="text-xs font-medium">Link URL</Label>
                          <Input value={msg.link_url || ''} onChange={e => updateMsg(idx, { link_url: e.target.value })} placeholder="https://…" className="mt-1 h-9" />
                        </div>
                        <div>
                          <Label className="text-xs font-medium">Button Label</Label>
                          <Input value={msg.link_button_label || ''} onChange={e => updateMsg(idx, { link_button_label: e.target.value })} placeholder="Learn more" className="mt-1 h-9" />
                        </div>
                      </div>
                    )}

                    {msg.type === 'calendar' && (
                      <div className="grid grid-cols-2 gap-2 p-3 rounded-lg bg-white border border-zinc-200">
                        <div className="col-span-2">
                          <Label className="text-xs font-medium">Booking URL (Calendly, Cal.com, …)</Label>
                          <Input value={msg.booking_url || ''} onChange={e => updateMsg(idx, { booking_url: e.target.value })} placeholder="https://calendly.com/your-link" className="mt-1 h-9" />
                        </div>
                        <div>
                          <Label className="text-xs font-medium">Button Label</Label>
                          <Input value={msg.button_label || ''} onChange={e => updateMsg(idx, { button_label: e.target.value })} placeholder="Pick a time" className="mt-1 h-9" />
                        </div>
                      </div>
                    )}

                    {msg.type === 'iframe' && (
                      <div className="grid grid-cols-3 gap-2 p-3 rounded-lg bg-white border border-zinc-200">
                        <div className="col-span-2">
                          <Label className="text-xs font-medium">Iframe URL</Label>
                          <Input value={msg.iframe_url || ''} onChange={e => updateMsg(idx, { iframe_url: e.target.value })} placeholder="https://calendly.com/your-link" className="mt-1 h-9" />
                        </div>
                        <div>
                          <Label className="text-xs font-medium">Height (px)</Label>
                          <Input type="number" value={msg.iframe_height || 360} onChange={e => updateMsg(idx, { iframe_height: parseInt(e.target.value) || 360 })} className="mt-1 h-9" />
                        </div>
                      </div>
                    )}

                    {msg.type === 'form' && (
                      <div className="grid grid-cols-2 gap-2 p-3 rounded-lg bg-white border border-zinc-200">
                        <div>
                          <Label className="text-xs font-medium">Form</Label>
                          <select value={msg.form_slug || ''} onChange={e => updateMsg(idx, { form_slug: e.target.value })} className="mt-1 w-full h-9 px-2 rounded-md border border-zinc-200 bg-white text-sm">
                            <option value="">— Pick a form —</option>
                            {forms.map(f => <option key={f.id} value={f.slug}>{f.title}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs font-medium">Button Label</Label>
                          <Input value={msg.button_label || ''} onChange={e => updateMsg(idx, { button_label: e.target.value })} placeholder="Open form" className="mt-1 h-9" />
                        </div>
                      </div>
                    )}

                    {/* Trigger row */}
                    <div className="grid grid-cols-3 gap-3 pt-2 border-t border-zinc-100">
                      <div>
                        <Label className="text-xs font-medium">Trigger</Label>
                        <select
                          value={msg.trigger || 'after_delay'}
                          onChange={e => updateMsg(idx, { trigger: e.target.value })}
                          className="mt-1 h-9 w-full px-2 rounded-md border border-zinc-200 bg-white text-sm"
                        >
                          <option value="after_delay">After delay</option>
                          <option value="first_visit">First visit only</option>
                          <option value="every_load">Every page load</option>
                          <option value="after_inactivity">After inactivity</option>
                        </select>
                      </div>
                      <div>
                        <Label className="text-xs font-medium">Delay (sec)</Label>
                        <Input type="number" min={0} max={300} value={msg.delay_seconds || 5} onChange={e => updateMsg(idx, { delay_seconds: parseInt(e.target.value) || 0 })} className="mt-1 h-9" />
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-2 text-xs mb-2">
                          <input type="checkbox" checked={msg.show_once_per_session !== false} onChange={e => updateMsg(idx, { show_once_per_session: e.target.checked })} className="rounded" />
                          Show once / session
                        </label>
                      </div>
                    </div>

                    <div className="flex justify-end pt-2">
                      <Button size="sm" variant="outline" onClick={() => setExpandedId(null)} data-testid={`proactive-collapse-${idx}`}>
                        <ChevronUp className="h-3.5 w-3.5 mr-1" />Collapse
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Add buttons — 5 types */}
      <div>
        <Label className="text-xs font-medium text-zinc-700 mb-2 block">Add a message</Label>
        <div className="grid grid-cols-5 gap-2">
          {PROACTIVE_TYPES.map(t => (
            <button
              key={t.id}
              onClick={() => addMsg(t.id)}
              className="flex flex-col items-center gap-1 p-3 rounded-xl border border-zinc-200 hover:border-violet-300 hover:bg-violet-50/30 transition-all"
              data-testid={`add-proactive-${t.id}`}
              title={t.desc}
            >
              <span className="text-xl">{t.icon}</span>
              <span className="text-xs font-semibold text-zinc-700">{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
        <div className="text-xs font-semibold text-blue-900 mb-1">🚀 Coming soon</div>
        <div className="text-xs text-blue-800">Per-URL targeting (e.g. only show this message on /pricing), audience filters (returning vs new visitor), and message analytics.</div>
      </div>
    </div>
  );
};

const TestBotSandbox = ({ subdomain, primaryColor, botName }) => {
  const [messages, setMessages] = React.useState([]);
  const [input, setInput] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [sessionId] = React.useState(() => `test-${Date.now()}`);
  const endRef = React.useRef(null);
  React.useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, loading]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || loading) return;
    const q = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: q }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/public/workspaces/${subdomain}/chat`, { message: q, session_id: sessionId });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response, sources: res.data.document_references || [] }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Error: ' + (err.response?.data?.detail || 'failed') }]);
    } finally { setLoading(false); }
  };

  const gradient = `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)`;

  return (
    <div className="border border-zinc-200 rounded-2xl overflow-hidden bg-white" data-testid="test-bot-sandbox">
      <div style={{ background: gradient }} className="px-4 py-3 text-white flex items-center gap-2">
        <Sparkles className="h-4 w-4" />
        <span className="font-semibold text-sm">{botName}</span>
        <span className="text-xs opacity-80 ml-2">live preview</span>
      </div>
      <div className="h-80 overflow-y-auto p-4 space-y-3 bg-zinc-50/50">
        {messages.length === 0 && (
          <div className="text-center text-sm text-zinc-400 py-8">Send a message to test how the bot responds.</div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.role === 'user' ? 'text-white' : 'bg-white border border-zinc-200 text-zinc-900'}`} style={m.role === 'user' ? { backgroundColor: primaryColor } : {}}>
              <div className="whitespace-pre-wrap">{m.content}</div>
              {m.sources && m.sources.length > 0 && (
                <div className="mt-2 pt-2 border-t border-zinc-100 text-xs text-zinc-500">
                  {m.sources.length} source{m.sources.length === 1 ? '' : 's'}: {m.sources.map(s => s.title).join(', ')}
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && <div className="text-xs text-zinc-400">Thinking…</div>}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex gap-2 p-3 border-t border-zinc-200">
        <Input value={input} onChange={e => setInput(e.target.value)} placeholder="Type a test question…" disabled={loading} data-testid="test-bot-input" />
        <Button type="submit" disabled={loading || !input.trim()} style={{ backgroundColor: primaryColor }} data-testid="test-bot-send">Send</Button>
      </form>
    </div>
  );
};

export default ChatbotSettings;
